import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  AllArticlesError,
  AllArticlesLoaded,
  AllArticlesRequested,
  ArticlesError,
  ArticlesForGestiuneConsumabileRequested,
  ArticlesForGestiuneConsumabileLoading,
  ArticlesForGestiuneConsumabileLoaded,
  ArticlesForGestiuneConsumabileError,
  ArticlesForGestiuneConsumabileNextPage,
  ArticlesForGestiuneConsumabileSearch,
  ArticlesForGestiuneError,
  ArticlesForGestiuneLoaded,
  ArticlesForGestiuneLoading,
  ArticlesForGestiuneNextPage,
  ArticlesForGestiuneRequested,
  ArticlesForGestiuneReset,
  ArticlesForGestiuneSearch,
  ArticlesLoaded,
  ArticlesRequested,
  ArticlesWithGestiuneError,
  ArticlesWithGestiuneLoaded,
  ArticlesWithGestiuneLoading,
  ArticlesWithGestiuneNextPage,
  ArticlesWithGestiuneRequested,
  ArticlesWithGestiuneSearch,
  ArticlesPaginatedNextPage,
  ArticlesPaginatedRequested,
  ArticlesPaginatedSearch,
  ArticlesPaginatedLoading,
  ArticlesPaginatedLoaded,
  ArticlesPaginatedError,
} from './articles.actions';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { concat, of } from 'rxjs';
import { ToastrConfigs } from '../../@core/data/toastr-config';
import { NbToastrService } from '@nebular/theme';
import { SagaService } from '../../@core/data/saga/saga.service';
import { Store } from '@ngrx/store';
import { IAppState } from '../app.state';
import { getArticlesState } from './articles.selectors';
import { IArticlesState } from './articles.state';

@Injectable()
export class ArticlesEffects {
  constructor(private actions$: Actions, private toastrService: NbToastrService, private sagaService: SagaService, private store: Store<IAppState>) {}

  requestArticles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesRequested),
      mergeMap(() => {
        return this.sagaService.getArticles().pipe(
          map((response: any) => ArticlesLoaded({ articles: response?.data })),
          catchError((error: any) => of(ArticlesError()))
        );
      })
    )
  );

  requestAllArticles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AllArticlesRequested),
      mergeMap(() =>
        this.sagaService.getArticles().pipe(
          map((response: any) => AllArticlesLoaded({ articles: response?.data })),
          catchError((error: any) => of(AllArticlesError()))
        )
      )
    )
  );

  requestArticlesForGestiune$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesForGestiuneRequested),
      withLatestFrom(this.store.select(getArticlesState)),
      mergeMap(([{ gestiuneId }, state]) =>
        concat(
          of(ArticlesForGestiuneLoading()),
          this.sagaService.getArticlesForGestiuneId(gestiuneId, state.articlesForGestiunePageSize, state.articlesForGestiunePage, state.articlesForGestiuneSearch).pipe(
            map((res: any) => {
              if (res?.data) {
                return ArticlesForGestiuneLoaded({ articlesForGestiune: res?.data || [] });
              }
              return ArticlesForGestiuneError();
            }),
            catchError(() => of(ArticlesForGestiuneError()))
          )
        )
      )
    )
  );

  nextPageArticlesPaginated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesPaginatedNextPage),
      mergeMap(() =>
        of(ArticlesPaginatedRequested())
      )
    )
  );

  articlesPaginatedSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesPaginatedSearch),
      mergeMap(() =>
        of(ArticlesPaginatedRequested())
      )
    )
  );
  
  requestArticlesPaginated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesPaginatedRequested),
      withLatestFrom(this.store.select(getArticlesState)),
      switchMap(([_, state]) =>
        concat(
          of(ArticlesPaginatedLoading()),
          this.sagaService.getArticolePaginate('true', state.articlesPaginatedSearch, state.articlesPaginatedPage, state.articlesPaginatedPageSize).pipe(
            map((res: any) => {
              if (res?.data) {
                return ArticlesPaginatedLoaded({ articlesPaginated: res?.data || [] });
              }
              return ArticlesPaginatedError();
            }),
            catchError(() => of(ArticlesPaginatedError()))
          )
        )
      )
    )
  );

  nextPageArticlesForGestiune$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesForGestiuneNextPage),
      mergeMap(({ gestiuneId }) =>
        of(ArticlesForGestiuneRequested({ gestiuneId }))
      )
    )
  );

  articlesForGestiuneSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesForGestiuneSearch),
      mergeMap(({ gestiuneId }) =>
        of(ArticlesForGestiuneRequested({ gestiuneId }))
      )
    )
  );
  
  requestArticlesForGestiuneConsumabile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesForGestiuneConsumabileRequested),
      withLatestFrom(this.store.select(getArticlesState)),
      mergeMap(([{ gestiuneId }, state]) =>
        concat(
          of(ArticlesForGestiuneConsumabileLoading()),
          this.sagaService
            .getArticlesForGestiuneConsumabile(
              gestiuneId,
              state.articlesForGestiuneConsumabilePageSize,
              state.articlesForGestiuneConsumabilePage,
              state.articlesForGestiuneConsumabileSearch
            )
            .pipe(
              map((res: any) =>
                res?.data
                  ? ArticlesForGestiuneConsumabileLoaded({ articlesForGestiune: res.data || [] })
                  : ArticlesForGestiuneConsumabileError()
              ),
              catchError(() => of(ArticlesForGestiuneConsumabileError()))
            )
        )
      )
    )
  );

  nextPageArticlesForGestiuneConsumabile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesForGestiuneConsumabileNextPage),
      mergeMap(({ gestiuneId }) =>
        of(ArticlesForGestiuneConsumabileRequested({ gestiuneId }))
      )
    )
  );

  articlesForGestiuneConsumabileSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesForGestiuneConsumabileSearch),
      mergeMap(({ gestiuneId }) =>
        of(ArticlesForGestiuneConsumabileRequested({ gestiuneId }))
      )
    )
  );

  onConsumabileError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ArticlesForGestiuneConsumabileError),
        tap(() => {
          this.toastrService.show(`Lista articolelor consumabile nu a putut fi încărcată!`, `Eroare`, ToastrConfigs.danger);
        })
      ),
    { dispatch: false }
  );

  requestArticlesWithGestiune$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesWithGestiuneRequested),
      withLatestFrom(this.store.select(getArticlesState)),
      mergeMap(([{}, state]) =>
        concat(
          of(ArticlesWithGestiuneLoading()),
          this.sagaService.getArticlesWithGestiune(state.articlesWithGestiunePageSize, state.articlesWithGestiunePage, state.articlesWithGestiuneSearch, true, state.onlySecondHand).pipe(
            map((res: any) => {
              if (res?.data) {
                return ArticlesWithGestiuneLoaded({ articlesWithGestiune: res?.data || [] });
              }
              return ArticlesWithGestiuneError();
            }),
            catchError(() => of(ArticlesWithGestiuneError()))
          )
        )
      )
    )
  );

  nextPageArticlesWithGestiune$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesWithGestiuneNextPage),
      mergeMap(() =>
        of(ArticlesWithGestiuneRequested())
      )
    )
  );

  articlesWithGestiuneSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesWithGestiuneSearch),
      mergeMap(() =>
        of(ArticlesWithGestiuneRequested())
      )
    )
  );

  onError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ArticlesError),
        tap(() => {
          this.toastrService.show(`Lista articolelor nu a putut fi încărcată!`, `Eroare`, ToastrConfigs.danger);
        })
      ),
    { dispatch: false }
  );
}
